// Commit a precise set of framework-owned paths and nothing else — the git
// primitive behind `autoRecord.commitFrameworkChanges` (see
// `commands/vortex.ts` → `runUpdate`). It exists so a `vortex update` that
// refreshes framework files + rewrites the ownership manifest can leave a CLEAN
// working tree instead of an uncommitted "trail" the next session would report
// as carried-over work.
//
// Safety is the whole point:
//   - it commits ONLY the given pathspec (a partial commit), so a user's
//     unrelated working-tree or already-staged changes are never swept in;
//   - it `git add`s those paths first so newly-written framework files
//     (`restore` / `install`) are included even though they start untracked;
//   - it is BEST-EFFORT: a non-git folder or any git failure is reported, never
//     thrown — the update command must still succeed.
//
// `execFileSync` with an argv array (no shell) means the paths — which come from
// the trusted shipped template index / ownership manifest, not user input — are
// passed as literal arguments, so there is no shell-injection surface.

import { execFileSync } from "node:child_process";

function git(repoRoot: string, args: readonly string[]): string {
  return execFileSync("git", [...args], {
    cwd: repoRoot,
    encoding: "utf8",
    // GIT_LITERAL_PATHSPECS=1 makes every `-- <path>` a LITERAL filename, not a
    // pathspec: it disables glob magic (`*` `?` `[…]`) and `:(…)` prefixes. So
    // even a path carrying those bytes (e.g. from a malformed template index)
    // can only ever match the exact file named — never a wider set.
    env: { ...process.env, GIT_LITERAL_PATHSPECS: "1" },
    // Suppress git's own stderr; the caller treats a non-zero exit as "no commit".
    stdio: ["ignore", "pipe", "ignore"],
  });
}

export interface CommitResult {
  readonly committed: boolean;
  /** Why no commit happened (when `committed` is false) — for diagnostics/tests. */
  readonly reason?: "no-paths" | "not-a-git-repo" | "nothing-to-commit" | "git-error";
  /** The error text when `reason === "git-error"`. */
  readonly error?: string;
}

/**
 * Commit exactly `paths` (repoRoot-relative) with `message`, leaving every other
 * change in the working tree or index untouched. Returns what happened; never
 * throws. No-op (and `committed:false`) when there are no paths, the folder is
 * not a git repo, or none of the paths actually changed.
 */
/**
 * The subset of `paths` (repoRoot-relative) that `git status --porcelain`
 * reports as changed (modified, deleted, or untracked). Pre-filtering with
 * this keeps a path that does not exist anywhere (e.g. `pnpm-lock.yaml` on an
 * npm instance) out of the later `git add`, which would otherwise fail the
 * WHOLE pathspec-limited commit. Best-effort: a git error drops the path.
 */
export function changedFrameworkPaths(repoRoot: string, paths: readonly string[]): string[] {
  const out: string[] = [];
  for (const p of paths) {
    try {
      if (git(repoRoot, ["status", "--porcelain", "--", p]).trim()) out.push(p);
    } catch {
      // unreadable status for this path — leave it uncommitted rather than guess
    }
  }
  return out;
}

/**
 * The committed (HEAD) text of `relPath` (repoRoot-relative, forward-slashed),
 * or `null` when it cannot be read — file not in HEAD, unborn HEAD (fresh repo
 * with no commits), or not a git repo. `null` means "no baseline to compare
 * against", so callers must NOT auto-commit on it.
 */
export function readHeadFileText(repoRoot: string, relPath: string): string | null {
  try {
    return git(repoRoot, ["show", `HEAD:${relPath}`]);
  } catch {
    return null;
  }
}

export function commitFrameworkPaths(
  repoRoot: string,
  paths: readonly string[],
  message: string,
): CommitResult {
  if (paths.length === 0) return { committed: false, reason: "no-paths" };
  try {
    git(repoRoot, ["rev-parse", "--git-dir"]);
  } catch {
    return { committed: false, reason: "not-a-git-repo" };
  }
  try {
    // Only commit if at least one of these paths has an actual change — avoids a
    // spurious empty commit and the "nothing to commit" error.
    const status = git(repoRoot, ["status", "--porcelain", "--", ...paths]).trim();
    if (!status) return { committed: false, reason: "nothing-to-commit" };
    // Stage exactly these paths (covers modified, newly-written, and deleted),
    // then commit ONLY this pathspec — a partial commit that ignores whatever
    // else may be staged, so unrelated user changes are never captured.
    git(repoRoot, ["add", "--", ...paths]);
    git(repoRoot, ["commit", "-m", message, "--", ...paths]);
    return { committed: true };
  } catch (e) {
    return { committed: false, reason: "git-error", error: (e as Error)?.message ?? String(e) };
  }
}
